'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorState {
  serviceType: string;
  propertySize: string;
  distance: string;
  packingService: boolean;
  assemblyService: boolean;
}

const PricingCalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [calculator, setCalculator] = useState<CalculatorState>({
    serviceType: 'residential',
    propertySize: '2bedroom',
    distance: 'zoneA',
    packingService: false,
    assemblyService: false,
  });
  const [estimatedPrice, setEstimatedPrice] = useState<number>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    calculatePrice();
  }, [calculator, isHydrated]);

  const calculatePrice = () => {
    let basePrice = 0;

    // Service type base
    if (calculator.serviceType === 'residential') basePrice = 50;
    else if (calculator.serviceType === 'commercial') basePrice = 80;

    // Property size multiplier
    const sizeMultipliers: { [key: string]: number } = {
      studio: 0.6,
      '1bedroom': 0.8,
      '2bedroom': 1.0,
      '3bedroom': 1.3,
      '4bedroom': 1.6,
      villa: 2.0,
      office: 1.5,
      warehouse: 2.5,
    };
    basePrice *= sizeMultipliers[calculator.propertySize] || 1.0;

    // Distance pricing
    const distancePricing: { [key: string]: number } = {
      zoneA: 1.0,
      zoneB: 1.2,
      zoneC: 1.4,
    };
    basePrice *= distancePricing[calculator.distance] || 1.0;

    // Additional services
    if (calculator.packingService) basePrice += 30;
    if (calculator.assemblyService) basePrice += 25;

    setEstimatedPrice(Math.round(basePrice));
  };

  const handleChange = (field: keyof CalculatorState, value: any) => {
    setCalculator((prev) => ({ ...prev, [field]: value }));
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-soft p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-soft overflow-hidden">
      <div className="bg-brand-purple p-6">
        <h2 className="text-2xl font-headline font-bold text-white">
          Quick Price Estimator
        </h2>
        <p className="text-white/80 mt-2">
          Get an instant estimate for your move
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Service Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleChange('serviceType', 'residential')}
              className={`p-4 rounded-lg border-2 transition-all duration-base ${
                calculator.serviceType === 'residential' ?'border-brand-purple bg-brand-purple/10' :'border-border hover:border-brand-purple/50'
              }`}
            >
              <Icon
                name="HomeIcon"
                size={24}
                variant="solid"
                className={
                  calculator.serviceType === 'residential' ?'text-brand-purple mx-auto mb-2' :'text-text-secondary mx-auto mb-2'
                }
              />
              <span className="block text-sm font-medium text-text-primary">
                Residential
              </span>
            </button>
            <button
              onClick={() => handleChange('serviceType', 'commercial')}
              className={`p-4 rounded-lg border-2 transition-all duration-base ${
                calculator.serviceType === 'commercial' ?'border-brand-purple bg-brand-purple/10' :'border-border hover:border-brand-purple/50'
              }`}
            >
              <Icon
                name="BuildingOfficeIcon"
                size={24}
                variant="solid"
                className={
                  calculator.serviceType === 'commercial' ?'text-brand-purple mx-auto mb-2' :'text-text-secondary mx-auto mb-2'
                }
              />
              <span className="block text-sm font-medium text-text-primary">
                Commercial
              </span>
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Property Size
          </label>
          <select
            value={calculator.propertySize}
            onChange={(e) => handleChange('propertySize', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-border bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-purple"
          >
            <option value="studio">Studio Apartment</option>
            <option value="1bedroom">1 Bedroom</option>
            <option value="2bedroom">2 Bedroom</option>
            <option value="3bedroom">3 Bedroom</option>
            <option value="4bedroom">4+ Bedroom</option>
            <option value="villa">Villa</option>
            <option value="office">Small Office</option>
            <option value="warehouse">Warehouse/Large Office</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Distance Zone
          </label>
          <select
            value={calculator.distance}
            onChange={(e) => handleChange('distance', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-border bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-purple"
          >
            <option value="zoneA">Zone A - Farwaniya Central</option>
            <option value="zoneB">Zone B - Extended Areas</option>
            <option value="zoneC">Zone C - Greater Kuwait</option>
          </select>
        </div>

        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={calculator.packingService}
              onChange={(e) =>
                handleChange('packingService', e.target.checked)
              }
              className="w-5 h-5 rounded border-border text-brand-purple focus:ring-brand-purple"
            />
            <span className="text-sm text-text-primary">
              Professional Packing Service (+30 KWD)
            </span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={calculator.assemblyService}
              onChange={(e) =>
                handleChange('assemblyService', e.target.checked)
              }
              className="w-5 h-5 rounded border-border text-brand-purple focus:ring-brand-purple"
            />
            <span className="text-sm text-text-primary">
              Furniture Assembly Service (+25 KWD)
            </span>
          </label>
        </div>

        <div className="bg-muted rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-text-secondary">Estimated Price:</span>
            <div className="flex items-baseline">
              <span className="text-4xl font-headline font-bold text-brand-purple">
                {estimatedPrice}
              </span>
              <span className="text-text-secondary ml-2">KWD</span>
            </div>
          </div>
          <p className="text-xs text-text-secondary">
            * Final price may vary based on actual requirements and site
            inspection
          </p>
        </div>

        <button className="w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold">
          <Icon name="PhoneIcon" size={20} variant="solid" />
          <span>Call 6991 1181 for Accurate Quote</span>
        </button>
      </div>
    </div>
  );
};

export default PricingCalculator;